import React, { Component } from 'react';
import { Field } from 'react-final-form';
import classNames from 'classnames';
import { injectIntl } from 'react-intl';
import { ValidationError } from '../../components';

import css from './UploadInput.module.css';

const isMessageDescriptor = val =>
  !!val && typeof val === 'object' && typeof val.id === 'string';

const formatMaybe = (intl, value) => {
  if (!value) return value;
  if (isMessageDescriptor(value)) {
    const { id, values } = value;
    return intl.formatMessage({ id }, values);
  }
  return value;
};

const isFile = v => {
  return typeof File !== 'undefined' && v instanceof File;
};

const UploadInputComponent = props => {
  const {
    rootClassName,
    className,
    inputRootClass,
    labelClassName,
    intl,
    label,
    chooseFileText,
    noFileText,
    viewFileText,
    customErrorText,
    existingFileUrl,
    id,
    input,
    meta,
    inputRef,
    hideErrorMessage,
    ...rest
  } = props;

  const labelText = formatMaybe(intl, label);

  if (labelText && !id) {
    throw new Error('id required when a label is given');
  }

  const { valid, invalid, touched, error } = meta;

  const errorText = formatMaybe(intl, customErrorText) || error;

  const hasError = !!customErrorText || !!(touched && invalid && error);
  const fieldMeta = { touched: hasError, error: errorText };

  const classes = classNames(rootClassName || css.root, className);

  const inputClasses =
    inputRootClass ||
    classNames(css.input, {
      [css.inputSuccess]: valid,
      [css.inputError]: hasError,
    });

  const labelClassMaybe = labelClassName ? { className: labelClassName } : {};
  const refMaybe = inputRef ? { ref: inputRef } : {};

  const { value, ...inputWithoutValue } = input;

  const chooseText =
    formatMaybe(intl, chooseFileText) ||
    intl.formatMessage({ id: 'UploadInput.chooseFile' });

  const emptyText =
    formatMaybe(intl, noFileText) ||
    intl.formatMessage({ id: 'UploadInput.noFileSelected' });

  const viewText =
    formatMaybe(intl, viewFileText) ||
    intl.formatMessage({ id: 'UploadInput.viewFile' });

  const selectedFileName = isFile(value) ? value.name : null;

  const showViewLink = !!existingFileUrl && !selectedFileName;

  return (
    <div className={`${classes} ${css.customField}`}>
      {labelText ? (
        <label htmlFor={id} {...labelClassMaybe}>
          {labelText}
        </label>
      ) : null}

      <input
        {...rest}
        {...refMaybe}
        {...inputWithoutValue}
        className={css.hiddenFileInput}
        id={id}
        type="file"
        multiple={false}
        onChange={e => {
          const files = e.target.files;
          input.onChange(files && files.length ? files[0] : null);
        }}
      />

      <div className={css.fileUi}>
        <label htmlFor={id} className={css.fileButton}>
          {chooseText}
        </label>

        <div className={css.fileName} aria-live="polite">
          {selectedFileName ? (
            selectedFileName
          ) : showViewLink ? (
            <a
              href={existingFileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={css.viewFileLink}
            >
              {viewText}
            </a>
          ) : (
            emptyText
          )}
        </div>
      </div>

      {hideErrorMessage ? null : <ValidationError fieldMeta={fieldMeta} />}
    </div>
  );
};

class UploadInput extends Component {
  componentWillUnmount() {
    if (this.props.onUnmount) {
      this.props.onUnmount();
    }
  }

  render() {
    return <Field component={UploadInputComponent} {...this.props} />;
  }
}

export default injectIntl(UploadInput);
